import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap';
import { fetchTableByIdRest, restartTableRest, startTableRest, stopTableRest } from '@/services/table/table-service';
import { showToast } from '@/common/showToast';
import checkVariants from '@/common/forms/table-handling/checkVariants';
import { PokerVariant } from '@repo/common-types';
import LoadingPage from '@/common/LoadingPage';
import { TableStatus } from '@/common/types/phenomTypes';
import routes from '@/routes/routes-enums';
import CustomSpinner from '@/components/spinner/CustomSpinner';
import StandardLayout from '@/common/layouts/StandardLayout';

import TableDetailsInfo from './components/TableDetailsDetails';
import TableDetailsGame from './components/TableDetailsGame';
import { useQuery } from '@tanstack/react-query';

const TableDetails: React.FC = () => {
  const { tableId } = useParams<{ tableId: string }>();

  const navigate = useNavigate();

  // const [table, setTable] = useState<TableDetailsDto | undefined>(undefined);
  const [isLimitGame, setIsLimitGame] = useState<boolean>(false);
  const [isStudGame, setIsStudGame] = useState<boolean>(true);
  // const [isLoading, setIsLoading] = useState<boolean>(false);
  const [details, setDetails] = useState<boolean>(false);
  const [restartTableConfirmOpen, setRestartTableConfirmOpen] = useState(false);
  const [restartGameserver, setRestartGameserver] = useState(false);

  const {
    data: table,
    isLoading,
    refetch: refreshTable,
  } = useQuery({
    queryKey: ['table-transactions', tableId],
    enabled: !!tableId,
    queryFn: () => fetchTableByIdRest(tableId || ''),
  });

  useEffect(() => {
    if (table?.variants && table.variants.length > 0) {
      const { stud, limit } = checkVariants(table.variants as PokerVariant[]);
      setIsStudGame(stud);
      setIsLimitGame(limit);
    }
  }, [table]);

  // const loadTable = useCallback(async () => {
  //   if (!tableId) return;
  //   try {
  //     const response = await fetchTableByIdRest(tableId, transactionsPagination);
  //     setTable(response);

  //     if (response.variants && response.variants.length > 0) {
  //       const { stud, limit } = checkVariants(response.variants as PokerVariant[]);
  //       setIsStudGame(stud);
  //       setIsLimitGame(limit);
  //     }
  //   } catch (error) {
  //     showToast(`Error when fetching server data: ${(error as AxiosError).message}`, 'warning');
  //   }
  // }, [tableId, transactionsPagination]);

  // useEffect(() => {
  //   loadTable();

  //   const interval = setInterval(() => {
  //     loadTable();
  //   }, 7000);

  //   // Cleanup interval on unmount
  //   return () => clearInterval(interval);
  // }, [loadTable, transactionsPagination]);

  // const refreshTable = async () => {
  //   await loadTable();
  // };

  const handleRestartTableConfirm = async () => {
    if (!tableId) return;
    // setIsLoading(true);
    try {
      await restartTableRest(tableId, restartGameserver);
      showToast('Request to restart table was made', 'success');
      refreshTable();
      // setTable((prevTable) => ({ ...prevTable!, status: TableStatus.RESTARTING }));
      setRestartTableConfirmOpen(false);
      setRestartGameserver(false); // Reset checkbox state
    } catch (error) {
      showToast(`Error restarting table: ${(error as Error).message}`, 'warning');
    } finally {
      // setIsLoading(false);
    }
  };

  const handleStartTable = async () => {
    if (!tableId) return;
    // setIsLoading(true);
    try {
      await startTableRest(tableId);
      showToast('Request to start table was made', 'success');
      refreshTable();

      // setTable((prevTable) => ({ ...prevTable!, status: TableStatus.INITIALIZING }));
    } catch (error) {
      showToast(`Error starting table: ${(error as Error).message}`, 'warning');
    } finally {
      // setIsLoading(false);
    }
  };

  const handleStopTable = async () => {
    if (!tableId) return;
    // setIsLoading(true);
    try {
      await stopTableRest(tableId, true);
      showToast('Request to stop table was made', 'success');
      refreshTable();

      // setTable((prevTable) => ({ ...prevTable!, status: TableStatus.STOPPING }));
    } catch (error) {
      showToast(`Error stopping table: ${(error as Error).message}`, 'warning');
    } finally {
      // setIsLoading(false);
    }
  };

  const getStatusColor = () => {
    switch (table?.status.toUpperCase()) {
      case TableStatus.RUNNING:
        return 'success';
      case TableStatus.STOPPED:
        return 'warning';
      case TableStatus.INITIALIZING:
      case TableStatus.STOPPING:
        return 'success';
      default:
        return 'secondary';
    }
  };

  const accessTable = () => {
    if (!tableId) return;
    const url = `${process.env.REACT_APP_PHENOM_APP}/table/${tableId}`;
    window.open(url, tableId, `height=768,width=1024`);
  };

  const openTableLogs = () => {
    if (!tableId) return;
    const base = 'https://telemetry.betterstack.com/team/t238083/tail?s=969735,969739,969749';
    const query = encodeURIComponent(`metadata.tableId=${tableId}`);
    const url = `${base}&q=${query}&rf=now-7d`;
    window.open(url, '_blank');
  };

  const goToTableEdit = () => {
    if (!tableId) return;
    navigate(`${routes.TABLE_EDIT}/${tableId}`);
  };

  if (isLoading || !table) {
    return <LoadingPage />;
  }

  return (
    <StandardLayout showBackButton pageTitle={table.name}>
      <Container fluid>
        <Row className="justify-content-center">
          <Col lg={12}>
            <Card className="justify-content-center p-3">
              <div className="mb-4">
                <div className={`bg-${getStatusColor()}-subtle position-relative`}>
                  <CardBody className="p-5">
                    <div className="text-center">
                      <h3>{table.name}</h3>
                      <h5>{table.game}</h5>
                      <p className="text-muted mb-0">{table.id}</p>
                    </div>
                  </CardBody>
                  <div className="shape">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      version="1.1"
                      xmlnsXlink="http://www.w3.org/1999/xlink"
                      width="1440"
                      height="60"
                      preserveAspectRatio="none"
                      viewBox="0 0 1440 60"
                    >
                      <g mask='url("#SvgjsMask1001")' fill="none">
                        <path
                          d="M 0,4 C 144,13 432,48 720,49 C 1008,50 1296,17 1440,9L1440 60L0 60z"
                          style={{ fill: 'var(--vz-secondary-bg)' }}
                        ></path>
                      </g>
                      <defs>
                        <mask id="SvgjsMask1001">
                          <rect width="1440" height="60" fill="#ffffff"></rect>
                        </mask>
                      </defs>
                    </svg>
                  </div>
                </div>

                {table.status.toUpperCase() && (
                  <div className="d-flex flex-rows justify-content-between align-items-center mt-3">
                    <Alert color={getStatusColor()}>
                      <div className="d-flex flex-rows justify-content-center gap-2 align-middle">
                        <span>Table {table.status.toUpperCase()}</span>
                        <div className="form-check form-switch form-switch-right form-switch-md d-flex justify-content-center ms-2 align-middle">
                          {(table.status.toUpperCase() === TableStatus.RUNNING ||
                            table.status.toUpperCase() === TableStatus.STOPPED) && (
                            <Input
                              className="form-check-input code-switcher ms-4"
                              type="checkbox"
                              checked={table.status.toUpperCase() === TableStatus.RUNNING}
                              onChange={
                                table.status.toUpperCase() === TableStatus.RUNNING ? handleStopTable : handleStartTable
                              }
                              disabled={isLoading}
                            />
                          )}
                          {(table.status.toUpperCase() === TableStatus.INITIALIZING ||
                            table.status.toUpperCase() === TableStatus.STOPPING) && (
                            <CustomSpinner color="warning" size="15px" />
                          )}
                        </div>
                      </div>
                    </Alert>

                    <div>
                      <div className="d-flex flex-rows items-align-center gap-4">
                        <Button onClick={() => setRestartTableConfirmOpen(true)}>
                          <i className="ri-error-warning-fill me-2"></i>
                          Restart
                        </Button>
                        <Button color="primary" onClick={accessTable}>
                          <i className="ri-link me-2"></i>
                          Open
                        </Button>
                        <Button color="primary" onClick={openTableLogs}>
                          <i className="ri-file-list-3-line me-2"></i>
                          Logs
                        </Button>
                        {details ? (
                          <Button color="primary" onClick={() => setDetails(false)}>
                            <i className="ri-nft-line me-2"></i>
                            Contract
                          </Button>
                        ) : (
                          <Button color="primary" onClick={() => setDetails(true)}>
                            <i className="ri-collage-fill me-2"></i>
                            Details
                          </Button>
                        )}
                        {table.status.toUpperCase() === TableStatus.STOPPED && (
                          <Button color="primary" onClick={goToTableEdit}>
                            <i className="ri-edit-2-line"></i>
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {details ? (
                <TableDetailsInfo table={table} isStudGame={isStudGame} isLimitGame={isLimitGame} />
              ) : (
                <TableDetailsGame
                  table={table}
                  isStudGame={isStudGame}
                  isLimitGame={isLimitGame}
                  refreshTable={refreshTable}
                />
              )}
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal
        isOpen={restartTableConfirmOpen}
        toggle={() => setRestartTableConfirmOpen(false)}
        id="confirm-restart-table"
        centered
      >
        <ModalHeader>Are you sure you want to restart this table?</ModalHeader>
        <ModalBody>
          <p>This will affect all current players on the table</p>
          <div className="form-check mt-3">
            <Input
              className="form-check-input border-primary"
              type="checkbox"
              id="restartGameserverCheck"
              checked={restartGameserver}
              onChange={(e) => setRestartGameserver(e.target.checked)}
            />
            <Label className="form-check-label text-muted ms-2" htmlFor="restartGameserverCheck">
              Hard restart gameserver <strong>(game play will be impacted)</strong>
            </Label>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="grey" onClick={() => setRestartTableConfirmOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleRestartTableConfirm} disabled={isLoading}>
            <i className="ri-error-warning-fill me-2"></i>
            Confirm
          </Button>
        </ModalFooter>
      </Modal>
    </StandardLayout>
  );
};

export default TableDetails;
